import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import {
  BehaviorSubject,
  Observable,
  catchError,
  filter,
  finalize,
  switchMap,
  take,
  throwError
} from 'rxjs';
import { AuthService } from '../auth/services/auth.service';
import { UtilsService } from '../services/utils/utils.service';

@Injectable()
export class GlobalInterceptor implements HttpInterceptor {
  private isRefreshing = false;
  private readonly tokenSubject = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly authService: AuthService,
    private readonly utils: UtilsService
  ) {}

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const token = this.authService.getToken();

    if (req.url.includes('/api/auth/login') || req.url.includes('/api/auth/refresh-token')) {
      return next.handle(req);
    }

    const clonedReq = token ? this.addAuthHeader(req, token) : req;

    return next.handle(clonedReq).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse){
          if(error.status === 401) {
            return this.handle401(clonedReq, next);
          } else if (error.status === 400) {
            this.authService.logout(); 
          }
        }
        return throwError(() => error);
      })
    );
  }

  private handle401(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    if (!this.isRefreshing) {
      this.isRefreshing = true;
      this.tokenSubject.next(null);

      return this.authService.refreshToken().pipe(
        switchMap((res) => {
          this.utils.saveAuthToken(res.access);
          this.tokenSubject.next(res.access);
          return next.handle(this.addAuthHeader(req, res.access));
        }),
        catchError(err => {
          this.authService.logout('session-expired');
          return throwError(() => err);
        }),
        finalize(() => {
          this.isRefreshing = false;
        })
      );
    } 
    return this.tokenSubject.pipe(
      filter(token => token !== null),
      take(1),
      switchMap(token => next.handle(this.addAuthHeader(req, token!)))
    );
  }

  private addAuthHeader(req: HttpRequest<any>, token: string): HttpRequest<any> {
    const headersConfig: any = {
      Authorization: `Bearer ${token}`,
      Accept: 'application/json',
      'Accept-Language': 'en', 
      'ngrok-skip-browser-warning': 'true'
    };

    if (!(req.body instanceof FormData)) {
      headersConfig['Content-Type'] = 'application/json';
    }

    return req.clone({
      setHeaders: headersConfig
    });
  }
}